import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Badge, Card, EmptyState, Stat, Table, Td } from '../ui/primitives';
import { classificationColour, pct, titleCase } from '../../lib/format';
import type { BotActivity } from '../../lib/types';

const CLASSIFICATION_FILL: Record<string, string> = {
  human: '#059669',
  machine_open: '#0284c7',
  suspected_bot: '#f59e0b',
  bot: '#dc2626',
  unknown: '#94a3b8',
};

/**
 * Automated-activity panel. This is the screen that answers the client's
 * questions about scanner filtering, so it shows the headline exclusion
 * shares, the breakdown by classification, every rule that fired and which
 * security vendors are scanning the mail.
 */
export function BotPanel({ data }: { data: BotActivity }) {
  const { summary } = data;

  const chartData = aggregateClassifications(data.classifications);

  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="Automated opens removed"
          value={pct(summary.automatedOpenSharePct)}
          hint={summary.opens.excluded.toLocaleString() + ' of ' + summary.opens.raw.toLocaleString() + ' recorded opens'}
          tone={summary.automatedOpenSharePct > 40 ? 'warn' : undefined}
        />
        <Stat
          label="Automated clicks removed"
          value={pct(summary.automatedClickSharePct)}
          hint={summary.clicks.excluded.toLocaleString() + ' of ' + summary.clicks.raw.toLocaleString() + ' recorded clicks'}
          tone={summary.automatedClickSharePct > 40 ? 'warn' : undefined}
        />
        <Stat
          label="Machine opens (Apple MPP)"
          value={summary.machineOpens.toLocaleString()}
          hint={pct(summary.machineOpenSharePct) + ' of recorded opens - pre-fetched, never read by a person'}
        />
        <Stat
          label="Proxied opens (Gmail)"
          value={summary.proxiedOpens.toLocaleString()}
          hint="Fetched through the Gmail image proxy - a real human open, flagged for transparency"
        />
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card
          title="How every recorded hit was classified"
          subtitle="Nothing is discarded at collection time. Filtering happens when the numbers are read, so both views stay available."
        >
          {chartData.length === 0 ? (
            <EmptyState title="No engagement recorded yet" />
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    tickLine={false}
                    interval={0}
                  />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }} />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {chartData.map((entry) => (
                      <Cell key={entry.classification} fill={CLASSIFICATION_FILL[entry.classification] ?? '#94a3b8'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card
          title="Security vendors seen scanning this mail"
          subtitle="Identified from the user agent or the source network of the request."
        >
          {data.scanners.length === 0 ? (
            <EmptyState
              title="No named scanner activity yet"
              body="Named vendors appear here as soon as a gateway follows a tracked link or fetches the pixel."
            />
          ) : (
            <Table head={['Vendor', 'Events', 'Recipients affected']}>
              {data.scanners.map((s) => (
                <tr key={s.scanner}>
                  <Td className="font-medium text-ink-900">{s.scanner}</Td>
                  <Td className="tabular-nums">{s.events.toLocaleString()}</Td>
                  <Td className="tabular-nums">{s.messages.toLocaleString()}</Td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      </div>

      <Card
        title="Which signal removed what"
        subtitle="Every rule is individually auditable, and every stored event records exactly which of them fired."
      >
        {data.rules.length === 0 ? (
          <EmptyState title="No rules have fired yet" />
        ) : (
          <Table head={['Rule', 'Times fired', 'What it detects']}>
            {data.rules.map((rule) => (
              <tr key={rule.ruleId}>
                <Td>
                  <div className="font-medium text-ink-900">{rule.label}</div>
                  <code className="text-xs text-ink-400">{rule.ruleId}</code>
                </Td>
                <Td className="tabular-nums">{rule.count.toLocaleString()}</Td>
                <Td className="max-w-xl text-xs leading-relaxed text-ink-500">{rule.description}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Card
        title="Full detection rule catalogue"
        subtitle="Everything the engine can detect, whether or not it has fired in this period."
      >
        <Table head={['Rule', 'Severity', 'Weight', 'Description']}>
          {data.catalogue.map((rule) => (
            <tr key={rule.id}>
              <Td className="font-medium text-ink-900">{rule.label}</Td>
              <Td>
                <Badge
                  tone={
                    rule.severity === 'hard'
                      ? 'bad'
                      : rule.severity === 'strong'
                        ? 'warn'
                        : rule.severity === 'weak'
                          ? 'neutral'
                          : 'info'
                  }
                >
                  {rule.severity}
                </Badge>
              </Td>
              <Td className="tabular-nums">{rule.weight}</Td>
              <Td className="max-w-2xl text-xs leading-relaxed text-ink-500">{rule.description}</Td>
            </tr>
          ))}
        </Table>
      </Card>
    </div>
  );
}

export function ClassificationBadge({ classification }: { classification: string }) {
  return (
    <span
      className={
        'inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ' +
        classificationColour(classification)
      }
    >
      {titleCase(classification)}
    </span>
  );
}

function aggregateClassifications(rows: Array<{ type: string; classification: string; count: number }>) {
  const totals = new Map<string, number>();
  for (const row of rows) {
    totals.set(row.classification, (totals.get(row.classification) ?? 0) + row.count);
  }
  return [...totals.entries()].map(([classification, count]) => ({
    classification,
    label: titleCase(classification),
    count,
  }));
}
